import useClassNames from '@medflex/hooks/useClassNames';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import isPermanentSession from '@medflex/utils/isPermanentSession';
import { SESSION_TYPES } from '@medflex/utils/constants';
import { IconCalendar, IconTime, IconUserGroup, IconUser } from '../Icons';
import Avatar from '../Avatar';
import Link from '../Link';

const ParticipantPill = ({ items = [], remaining = 0, url, ...props }) => {
  const { t } = useTranslation('components');
  return (
    <div data-component="ParticipantPill" {...props} className="text-slate-600 font-normal">
      {url ? (
        <Link to={url} className="font-normal text-slate-600 hover:text-slate-600">
          {items.join(', ')}
        </Link>
      ) : (
        <span className="font-normal">{items.join(', ')}</span>
      )}

      {remaining > 0 && (
        <span className="ml-2 bg-slate-100 rounded px-2">
          {t('Sessions.SessionsListItem.remainingParticipants', { remaining })}
        </span>
      )}
    </div>
  );
};

ParticipantPill.propTypes = {
  items: PropTypes.arrayOf(PropTypes.string),
  remaining: PropTypes.number,
  url: PropTypes.string,
};

const FallbackIconWrapper = ({ children }) => (
  <div className="flex justify-center items-center rounded-full border w-12 h-12">{children}</div>
);

const SessionsListItem = ({
  scheduledAt,
  details,
  actions,
  className,
  attendee,
  duration,
  hasDurationCol,
  scheduledEndedAt,
  type,
}) => {
  const { t } = useTranslation('components');
  const cn = useClassNames(
    () => [
      'flex',
      'flex-col',
      'p-6',
      'mb-4',
      'border-l-4',
      'overflow-hidden',
      'bg-blue-100',
      'lg:mb-0',
      'last:mb-0',
      'lg:flex-row',
      'lg:border-l-0',
      'lg:bg-white',
      className,
    ],
    [className],
  );

  return (
    <li data-component="SessionsListItem" className={cn}>
      <div className="w-24 flex justify-center flex-shrink-0">
        <div className="hidden lg:flex ml-4 justify-center items-center text-slate-400">
          {!attendee && (
            <FallbackIconWrapper>
              <IconUserGroup />
            </FallbackIconWrapper>
          )}
          {attendee &&
            (attendee.initials ? (
              <Avatar
                size="12"
                alt={attendee.initials}
                src={attendee.avatar}
                initials={attendee.initials}
              />
            ) : (
              <FallbackIconWrapper>
                <IconUser />
              </FallbackIconWrapper>
            ))}
        </div>
      </div>
      <div className="flex mb-2 lg:w-2/12 lg:flex-col lg:mb-0 flex-shrink-0 lg:px-4 lg:justify-center">
        {!isPermanentSession({ scheduledAt, type }) ? (
          <>
            <div className="flex items-center">
              <span className="hidden lg:inline-flex text-slate-600">
                {t('Sessions.SessionsListItem.onDate')}
              </span>
              <IconCalendar className="lg:hidden text-slate-600" size="text" />
              <span className="ml-2 lg:font-semibold">{format(scheduledAt, 'DD.MM.YYYY')}</span>
            </div>
            <div className="flex items-center ml-4 lg:ml-0">
              <span className="hidden lg:inline-flex text-slate-600">
                {t('Sessions.SessionsListItem.atTime')}
              </span>
              <IconTime className="lg:hidden text-slate-600" size="text" />
              <span className="ml-2 lg:font-semibold">
                {format(scheduledAt, 'HH:mm')}
                {scheduledEndedAt && ` - ${format(new Date(scheduledEndedAt), 'HH:mm')}`}
              </span>
            </div>
          </>
        ) : (
          t('Sessions.SessionsListItem.permanentSession')
        )}
      </div>
      {hasDurationCol && (
        <div className="flex mb-2 lg:w-2/12 lg:flex-col lg:justify-between lg:mb-0 flex-shrink-0 lg:px-4">
          <div className="flex">
            {duration ? (
              <div className="flex">
                <div className="lg:hidden mr-1">{t('Sessions.SessionsListItem.duration')}</div>
                {duration}
              </div>
            ) : (
              <div className="italic text-slate-600">{t('Sessions.SessionsListItem.missed')}</div>
            )}
          </div>
        </div>
      )}
      <div className="flex flex-col flex-auto justify-between overflow-hidden lg:px-4">
        {details}
      </div>
      {actions && (
        <div className="flex flex-wrap justify-end lg:self-center mt-3 lg:mt-0 flex-shrink-0 lg:px-4">
          {actions}
        </div>
      )}
    </li>
  );
};

SessionsListItem.propTypes = {
  details: PropTypes.node,
  scheduledAt: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  scheduledEndedAt: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  actions: PropTypes.node,
  duration: PropTypes.string,
  hasDurationCol: PropTypes.bool,
  attendee: PropTypes.shape({
    initials: PropTypes.string,
    avatar: PropTypes.string,
  }),
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.array,
    PropTypes.object,
  ]),
  type: PropTypes.oneOf(Object.values(SESSION_TYPES)),
};

export default SessionsListItem;
export { ParticipantPill };
